<script setup>
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['submit']);

const { t } = useI18n();

const CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const LANGUAGES = ['pt_BR', 'en_US', 'en', 'es', 'es_ES'];
const HEADER_FORMATS = ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'];
const BUTTON_TYPES = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER'];
const MEDIA_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];
const NAME_REGEX = /^[a-z0-9_]+$/;
const VARIABLE_REGEX = /\{\{(\d+)\}\}/g;

const dialogRef = ref(null);

const form = reactive({
  name: '',
  language: 'pt_BR',
  category: 'MARKETING',
  header: { format: 'NONE', text: '', mediaUrl: '' },
  body: '',
  footer: '',
  buttons: [],
});

const errors = reactive({ name: '', body: '' });

const selectClass =
  'w-full h-10 px-3 text-sm rounded-xl outline-1 outline outline-n-weak bg-n-background text-n-slate-12';

const isMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(form.header.format)
);

const resetForm = () => {
  form.name = '';
  form.language = 'pt_BR';
  form.category = 'MARKETING';
  form.header = { format: 'NONE', text: '', mediaUrl: '' };
  form.body = '';
  form.footer = '';
  form.buttons = [];
  errors.name = '';
  errors.body = '';
};

const open = () => {
  resetForm();
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

defineExpose({ open, close });

const addButton = () => {
  form.buttons.push({
    type: 'QUICK_REPLY',
    text: '',
    url: '',
    phoneNumber: '',
  });
};

const removeButton = index => {
  form.buttons.splice(index, 1);
};

const buildExample = (text, key) => {
  const matches = [...text.matchAll(VARIABLE_REGEX)];
  if (!matches.length) return null;
  const samples = matches.map((_, idx) => `exemplo${idx + 1}`);
  return key === 'body_text' ? { [key]: [samples] } : { [key]: samples };
};

const mapButton = button => {
  if (button.type === 'URL') {
    return { type: 'URL', text: button.text, url: button.url };
  }
  if (button.type === 'PHONE_NUMBER') {
    return {
      type: 'PHONE_NUMBER',
      text: button.text,
      phone_number: button.phoneNumber,
    };
  }
  return { type: 'QUICK_REPLY', text: button.text };
};

const buildComponents = () => {
  const components = [];

  if (form.header.format === 'TEXT' && form.header.text) {
    const header = { type: 'HEADER', format: 'TEXT', text: form.header.text };
    const example = buildExample(form.header.text, 'header_text');
    if (example) header.example = example;
    components.push(header);
  } else if (isMediaHeader.value && form.header.mediaUrl) {
    components.push({
      type: 'HEADER',
      format: form.header.format,
      media_url: form.header.mediaUrl,
    });
  }

  const body = { type: 'BODY', text: form.body };
  const bodyExample = buildExample(form.body, 'body_text');
  if (bodyExample) body.example = bodyExample;
  components.push(body);

  if (form.footer) components.push({ type: 'FOOTER', text: form.footer });

  if (form.buttons.length) {
    components.push({
      type: 'BUTTONS',
      buttons: form.buttons.map(button => mapButton(button)),
    });
  }

  return components;
};

const validate = () => {
  errors.name = NAME_REGEX.test(form.name)
    ? ''
    : t('WHATSAPP_SETTINGS.TEMPLATES.FORM.NAME_INVALID');
  errors.body = form.body.trim()
    ? ''
    : t('WHATSAPP_SETTINGS.TEMPLATES.FORM.BODY_REQUIRED');
  return !errors.name && !errors.body;
};

const onConfirm = () => {
  if (!validate()) return;
  emit('submit', {
    name: form.name,
    language: form.language,
    category: form.category,
    components: buildComponents(),
  });
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('WHATSAPP_SETTINGS.TEMPLATES.FORM.TITLE')"
    :confirm-button-label="t('WHATSAPP_SETTINGS.TEMPLATES.FORM.SUBMIT')"
    width="2xl"
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="form.name"
        :label="t('WHATSAPP_SETTINGS.TEMPLATES.FORM.NAME')"
        :placeholder="t('WHATSAPP_SETTINGS.TEMPLATES.FORM.NAME_PLACEHOLDER')"
        :message="errors.name"
        :message-type="errors.name ? 'error' : 'info'"
      />

      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('WHATSAPP_SETTINGS.TEMPLATES.FORM.CATEGORY') }}
          </label>
          <select v-model="form.category" :class="selectClass">
            <option
              v-for="category in CATEGORIES"
              :key="category"
              :value="category"
            >
              {{ category }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('WHATSAPP_SETTINGS.TEMPLATES.FORM.LANGUAGE') }}
          </label>
          <select v-model="form.language" :class="selectClass">
            <option
              v-for="language in LANGUAGES"
              :key="language"
              :value="language"
            >
              {{ language }}
            </option>
          </select>
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WHATSAPP_SETTINGS.TEMPLATES.FORM.HEADER') }}
        </label>
        <select v-model="form.header.format" :class="selectClass">
          <option
            v-for="format in HEADER_FORMATS"
            :key="format"
            :value="format"
          >
            {{ format }}
          </option>
        </select>
        <Input
          v-if="form.header.format === 'TEXT'"
          v-model="form.header.text"
          class="mt-2"
          :placeholder="
            t('WHATSAPP_SETTINGS.TEMPLATES.FORM.HEADER_TEXT_PLACEHOLDER')
          "
        />
        <Input
          v-if="isMediaHeader"
          v-model="form.header.mediaUrl"
          class="mt-2"
          :placeholder="
            t('WHATSAPP_SETTINGS.TEMPLATES.FORM.HEADER_MEDIA_PLACEHOLDER')
          "
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WHATSAPP_SETTINGS.TEMPLATES.FORM.BODY') }}
        </label>
        <textarea
          v-model="form.body"
          rows="4"
          :placeholder="t('WHATSAPP_SETTINGS.TEMPLATES.FORM.BODY_PLACEHOLDER')"
          class="w-full p-3 text-sm rounded-xl outline-1 outline outline-n-weak bg-n-background text-n-slate-12"
        />
        <span v-if="errors.body" class="text-xs text-n-ruby-11">{{
          errors.body
        }}</span>
        <span class="text-xs text-n-slate-10">
          {{ t('WHATSAPP_SETTINGS.TEMPLATES.FORM.VARIABLE_HINT') }}
        </span>
      </div>

      <Input
        v-model="form.footer"
        :label="t('WHATSAPP_SETTINGS.TEMPLATES.FORM.FOOTER')"
        :placeholder="t('WHATSAPP_SETTINGS.TEMPLATES.FORM.FOOTER_PLACEHOLDER')"
      />

      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('WHATSAPP_SETTINGS.TEMPLATES.FORM.BUTTONS') }}
          </label>
          <Button
            size="sm"
            variant="ghost"
            icon="i-lucide-plus"
            :label="t('WHATSAPP_SETTINGS.TEMPLATES.FORM.ADD_BUTTON')"
            @click="addButton"
          />
        </div>
        <div
          v-for="(button, index) in form.buttons"
          :key="index"
          class="flex flex-col gap-2 p-3 rounded-xl outline-1 outline outline-n-weak"
        >
          <div class="flex gap-2">
            <select v-model="button.type" :class="selectClass">
              <option v-for="type in BUTTON_TYPES" :key="type" :value="type">
                {{ type }}
              </option>
            </select>
            <Button
              size="sm"
              variant="ghost"
              color="ruby"
              icon="i-lucide-trash-2"
              @click="removeButton(index)"
            />
          </div>
          <Input
            v-model="button.text"
            :placeholder="t('WHATSAPP_SETTINGS.TEMPLATES.FORM.BUTTON_TEXT')"
          />
          <Input
            v-if="button.type === 'URL'"
            v-model="button.url"
            placeholder="https://..."
          />
          <Input
            v-if="button.type === 'PHONE_NUMBER'"
            v-model="button.phoneNumber"
            placeholder="+5511999999999"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>
